import React, { useMemo, useEffect } from 'react'
import Layout from '../Layout/Layout'
import Pagination from '../molecules/Pagination'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootReducer } from '../../redux/rootReducer'
import { SET_SORT, SET_SEARCH, SET_PAGE, FETCH_ESTIMATES, type EstimatesSortType } from '../../redux/estimatesReducer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMoneyBill } from '@fortawesome/free-solid-svg-icons'
import { getBackgroundColors } from 'src/helpers/getBackgroundColros'

const EstimatesScreen = (): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const sort = useSelector((state: RootReducer) => state.estimatesReducer.sort)
  const currentPage = useSelector((state: RootReducer) => state.estimatesReducer.currentPage)
  const search = useSelector((state: RootReducer) => state.estimatesReducer.search)
  const estimates = useSelector((state: RootReducer) => state.estimatesReducer.estimates)
  const dispatch = useDispatch()

  const handleSortChange = (colName: 'created_at' | 'full_name' | 'email' | 'status' | 'zip_from' | 'zip_to'): void => {
    const isCurrentDirAsc = (sort as Record<string, string>)[colName] === 'asc'
    searchParams.set('sort', `${colName}=${isCurrentDirAsc ? 'desc' : 'asc'}`)
    searchParams.set('page', '1')
    navigate(`?${searchParams.toString()}`)
  }

  useEffect(() => {
    const page = searchParams.get('page') ?? '1'
    const [sortColumn, sortDirection] = (searchParams.get('sort') ?? 'created_at=desc').split('=')
    // Dynamic sort key from URL; type assertion required for union type
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const setSortPayload = { [sortColumn]: sortDirection } as EstimatesSortType
    dispatch(SET_SORT(setSortPayload))
    const email = searchParams.get('email')
    const dateMin = searchParams.get('dateMin')
    const dateMax = searchParams.get('dateMax')
    const phone = searchParams.get('phone')
    const city = searchParams.get('city')
    const zip = searchParams.get('zip')
    const status = searchParams.get('status')
    dispatch(
      SET_SEARCH({
        email: email ?? '',
        date_min: dateMin ?? '',
        date_max: dateMax ?? '',
        phone: phone ?? '',
        city: city ?? '',
        zip: zip ?? '',
        status: status ?? ''
      })
    )
    dispatch(SET_PAGE({ page: parseInt(page, 10) }))
  }, [location.search, dispatch, searchParams])

  useEffect(() => {
    dispatch(FETCH_ESTIMATES())
  }, [dispatch, currentPage, sort, search])

  return (
    <Layout withFilter>
      <div className="bg-white rounded-sm w-full overflow-hidden">
        <div className="border-b-4 border-orange" />
        <div className="p-3">
          <table className="w-full text-14">
            <thead>
              <tr>
                <td
                  onClick={() => {
                    handleSortChange('created_at')
                  }}
                  className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">
                  Anfrage vom
                </td>
                <td
                  onClick={() => {
                    handleSortChange('full_name')
                  }}
                  className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">
                  Name
                </td>
                <td
                  onClick={() => {
                    handleSortChange('email')
                  }}
                  className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">
                  Email
                </td>
                <td className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">Telefon</td>
                <td
                  onClick={() => {
                    handleSortChange('zip_from')
                  }}
                  className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">
                  PLZ (start)
                </td>
                <td className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">
                  Stadt (start)
                </td>
                <td
                  onClick={() => {
                    handleSortChange('zip_to')
                  }}
                  className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">
                  PLZ (Ziel)
                </td>
                <td className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">
                  Stadt (Ziel)
                </td>
                <td className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">
                  Marke / Modell
                </td>
                <td
                  onClick={() => {
                    handleSortChange('status')
                  }}
                  className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">
                  Status
                </td>
                <td className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer">Preis</td>
                <td className="p-2 border border-[#f4f4f4] text-16 font-semibold text-blue cursor-pointer" />
              </tr>
            </thead>
            <tbody>
              {estimates.map(
                (
                  {
                    id,
                    created_at,
                    full_name,
                    email,
                    first_phone,
                    zip_from,
                    city_from,
                    zip_to,
                    city_to,
                    make,
                    model,
                    status,
                    price,
                    express_transport,
                    make_payment,
                    premium_transport
                  },
                  idx
                ) => (
                  <tr
                    key={id}
                    style={{ background: getBackgroundColors(express_transport, premium_transport) }}
                    className={idx % 2 === 0 ? 'bg-grayBackground' : 'bg-white'}>
                    <td className="p-2 border border-[#f4f4f4]">{created_at}</td>
                    <td className="p-2 border border-[#f4f4f4]">
                      {full_name}
                    </td>
                    <td className="p-2 border border-[#f4f4f4]">{email}</td>
                    <td className="p-2 border border-[#f4f4f4]">{first_phone}</td>
                    <td className="p-2 border border-[#f4f4f4]">{zip_from}</td>
                    <td className="p-2 border border-[#f4f4f4]">{city_from}</td>
                    <td className="p-2 border border-[#f4f4f4]">{zip_to}</td>
                    <td className="p-2 border border-[#f4f4f4]">{city_to}</td>
                    <td className="p-2 border border-[#f4f4f4]">
                      {make} {model}
                    </td>
                    <td className="p-2 border border-[#f4f4f4]">{status}</td>
                    <td className="p-2 border border-[#f4f4f4]">{price}</td>
                    <td className="p-2 border border-[#f4f4f4]">
                      {make_payment && <FontAwesomeIcon icon={faMoneyBill} />}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
          <Pagination reducer="estimatesReducer" />
        </div>
      </div>
    </Layout>
  )
}

export default EstimatesScreen
